import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { Textarea } from '../ui/Textarea'
import { Accordion } from '../ui/Accordion'
import { RecurrencePicker } from '../recurring/RecurrencePicker'
import type { RecurrenceRule } from '../recurring/RecurrencePicker'
import { ParentPicker } from './ParentPicker'
import type { ParentType } from './ParentPicker'
import { PlacePicker } from '../places/PlacePicker'
import { AlertCircle, ArrowLeft, Sparkles } from 'lucide-react'
import { QuietHoursNotice } from './QuietHoursNotice'
import { cn } from '../../utils/cn'
import { AddOwnChip } from '../ui/AddOwnChip'

/** Fields sharing a group render under one small heading: a section says once
 * what a per-field tag used to repeat on every row ("helps me plan it"). */
export interface FieldGroup { key: string; title: string; blurb?: string }

export interface FieldConfig {
 name: string
 label: string
 type: 'text' | 'textarea' | 'select' | 'date' | 'time' | 'number' | 'duration' | 'parent_picker' | 'recurrence' | 'place_picker' | 'chips' | 'custom'
 required?: boolean
 placeholder?: string
 options?: { value: string; label: string }[]
 allowedParentTypes?: ParentType[]
 min?: number
 max?: number
 rows?: number
 /** One quiet line under the input. Used by the deadline fields to explain
  * what setting a time actually does. */
 helpText?: string
 /** A small tag beside the label, e.g. "helps me plan it" (contract Tier B). */
 hint?: string
 /** Render only when true — a stakes row that appears once a date is set. */
 showWhen?: (formData: Record<string, any>) => boolean
 /** A soft nudge under the input computed from the current value; never blocks. */
 liveHint?: (value: any, formData: Record<string, any>) => string | null
 /** chips: write the option as a number (durations) rather than a string. */
 numeric?: boolean
 /** chips: offer a "More…" chip that opens the hours/minutes inputs. */
 customDuration?: boolean
 /** Laptop layout (lg+): 'half' shares a row with its neighbour, 'full' takes
  * the row. Default full — forms that never set it are unchanged. */
 span?: 'full' | 'half'
 /** chips: several may be picked; the value is an array in pick order (first = primary). */
 multi?: boolean
 /** chips + multi: a "+ Another…" chip lets the person add a value of their own.
  * Added values live in formData[`${name}__extra`] so they keep rendering as chips. */
 allowCustom?: boolean
 customLabel?: string
 customPlaceholder?: string
 /** How a typed name becomes a stored value (a life area's slug). Default: trimmed text. */
 customValue?: (text: string) => string
 /** Words for a value that is not among the options (a person's own life area). */
 labelOf?: (value: string) => string
 group?: FieldGroup
 /** custom: the field renders itself from the whole form state (a habit's schedule chooser). */
 // eslint-disable-next-line @typescript-eslint/no-explicit-any
 render?: (formData: Record<string, any>, onFormDataChange: (data: Record<string, any>) => void) => React.ReactNode
}

export interface BasicField extends FieldConfig {
 section: 'basic'
}

export interface AdvancedField extends FieldConfig {
 section: 'advanced'
}

interface HierarchyCreationFormProps {
 title: string
 description: string
 icon?: React.ReactNode
 /** Owner 2026-09-05: the same voice as the item pages — a breadcrumb, a plain
  * title and one muted line instead of the gradient hero; no section heading. */
 compactHeader?: boolean
 basicFields: BasicField[]
 advancedFields?: AdvancedField[]
 formData: Record<string, any>
 onFormDataChange: (data: Record<string, any>) => void
 onSubmit: (e: React.FormEvent) => void
 onCancel?: () => void
 submitLabel?: string
 cancelLabel?: string
 /** Heading of the collapsed section. Tasks call it "More". */
 advancedTitle?: string
 error?: string | null
 primaryColor?: string
 backUrl?: string
 isLoading?: boolean
}

export function HierarchyCreationForm({
 title,
 description,
 icon,
 basicFields,
 advancedFields = [],
 formData,
 onFormDataChange,
 onSubmit,
 onCancel,
 submitLabel = 'Create',
 cancelLabel = 'Cancel',
 advancedTitle = 'Advanced Options',
 error,
 primaryColor = 'purple',
 backUrl,
 isLoading = false,
 compactHeader = false
}: HierarchyCreationFormProps) {
 // Required-field errors appear after the first attempt to save, not on a
 // form nobody has typed in yet (look-and-feel pass 2026-09-05).
 const [attempted, setAttempted] = useState(false)
 // Only a form that pairs fields (span: 'half') earns the wider laptop
 // container; a single-column form at 1150px is just long input boxes.
 const twoColumn = [...basicFields, ...advancedFields].some((f) => f.span === 'half')
 const visibleBasic = basicFields.filter((field) => !field.showWhen || field.showWhen(formData))
 const navigate = useNavigate()

 const handleFieldChange = (name: string, value: any) => {
 onFormDataChange({ ...formData, [name]: value })
 }

 const handleCancel = () => {
 if (onCancel) {
 onCancel()
 } else if (backUrl) {
 navigate(backUrl)
 } else {
 navigate(-1)
 }
 }

 const renderField = (field: FieldConfig) => {
 const value = formData[field.name] || ''
 const isRequired = field.required
 const hasError = isRequired && !value && error

 const focusRingClasses = {
 purple: 'focus:ring-info',
 blue: 'focus:ring-brand',
 green: 'focus:ring-ok',
 indigo: 'focus:ring-info',
 orange: 'focus:ring-warn',
 }
 const focusRingClass = focusRingClasses[primaryColor as keyof typeof focusRingClasses] || 'focus:ring-info'

 const baseInputClasses = cn(
 'w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200',
 hasError
 ? 'border-bad focus:ring-bad bg-tint-bad-soft'
 : `border-strong ${focusRingClass} hover:border-strong`
 )

 switch (field.type) {
 case 'textarea':
 return (
 <Textarea
 name={field.name}
 value={value}
 onChange={(e) => handleFieldChange(field.name, e.target.value)}
 placeholder={field.placeholder}
 rows={field.rows || 4}
 className={baseInputClasses}
 required={isRequired}
 />
 )
 case 'select':
 return (
 <select
 name={field.name}
 value={value}
 onChange={(e) => handleFieldChange(field.name, e.target.value)}
 className={baseInputClasses}
 required={isRequired}
 >
 {field.options?.map((option) => (
 <option key={option.value} value={option.value}>
 {option.label}
 </option>
 ))}
 </select>
 )
 case 'time':
   // A clock time, stored as plain "HH:MM" local. Kept separate from
   // 'date' on purpose — that case splits on 'T' to strip a datetime,
   // which would mangle a time value.
   return (
     <input
       type="time"
       name={field.name}
       value={typeof value === 'string' ? value : ''}
       onChange={(e) => handleFieldChange(field.name, e.target.value || undefined)}
       className={baseInputClasses}
       required={isRequired}
     />
   )

 case 'date':
 // Extract only the date portion (yyyy-MM-dd) for HTML date input
 // Handles both"2026-01-06" and"2026-01-06T06:00:00" formats
 const dateValue = typeof value === 'string' && value.includes('T') 
 ? value.split('T')[0] 
 : value
 return (
 <input
 type="date"
 name={field.name}
 value={dateValue}
 onChange={(e) => handleFieldChange(field.name, e.target.value || undefined)}
 className={baseInputClasses}
 required={isRequired}
 />
 )
 case 'number':
 return (
 <input
 type="number"
 name={field.name}
 value={value}
 onChange={(e) => handleFieldChange(field.name, parseInt(e.target.value) || 0)}
 min={field.min}
 max={field.max}
 className={baseInputClasses}
 required={isRequired}
 />
 )
 case 'duration': {
 const totalMinutes = typeof value === 'number' ? value : (parseInt(value) || 0)
 const hours = Math.floor(totalMinutes / 60)
 const mins = totalMinutes % 60
 return (
 <div className="flex items-center gap-3">
   <div className="flex-1">
     <label className="block text-xs text-muted mb-1">Hours</label>
     <input
       type="number"
       min={0}
       max={999}
       value={hours || ''}
       placeholder="0"
       onChange={(e) => {
         const h = parseInt(e.target.value) || 0
         handleFieldChange(field.name, h * 60 + mins)
       }}
       className={baseInputClasses}
     />
   </div>
   <span className="text-lg font-medium text-muted pt-5">:</span>
   <div className="flex-1">
     <label className="block text-xs text-muted mb-1">Minutes</label>
     <input
       type="number"
       min={0}
       max={59}
       value={mins || ''}
       placeholder="0"
       onChange={(e) => {
         const m = Math.min(parseInt(e.target.value) || 0, 59)
         handleFieldChange(field.name, hours * 60 + m)
       }}
       className={baseInputClasses}
     />
   </div>
 </div>
 )
 }
 case 'chips': {
   // One row of choices, tap to pick, tap again to clear. Optional fields
   // stay optional: an untouched row sends nothing (contract §1).
   const multiValues: string[] = field.multi ? (Array.isArray(value) ? value.map(String) : (value ? [String(value)] : [])) : []
   const extraKey = `${field.name}__extra`
   const extraValues: string[] = Array.isArray(formData[extraKey]) ? formData[extraKey].map(String) : []
   const chipOptions = [
     ...(field.options || []),
     ...extraValues.filter(v => !(field.options || []).some(o => o.value === v)).map(v => ({ value: v, label: field.labelOf ? field.labelOf(v) : v })),
   ]
   const addOwn = (text: string) => {
     const v = field.customValue ? field.customValue(text) : text.trim()
     if (!v) return
     const nextExtra = extraValues.includes(v) ? extraValues : [...extraValues, v]
     const nextValues = multiValues.includes(v) ? multiValues : [...multiValues, v]
     onFormDataChange({ ...formData, [field.name]: nextValues, [extraKey]: nextExtra })
   }
   const str = value === '' || value == null || Array.isArray(value) ? '' : String(value)
   const inOptions = (field.options || []).some(o => o.value === str)
   const customKey = `${field.name}__custom`
   const customOpen = Boolean(field.customDuration && (formData[customKey] || (str !== '' && !inOptions)))
   const pick = (v: string) => {
     if (field.multi) {
       const next = multiValues.includes(v) ? multiValues.filter(x => x !== v) : [...multiValues, v]
       onFormDataChange({ ...formData, [field.name]: next })
       return
     }
     const next = str === v ? '' : v
     onFormDataChange({ ...formData, [field.name]: field.numeric ? (next === '' ? '' : Number(next)) : next, [customKey]: false })
   }
   const totalMinutes = typeof value === 'number' ? value : (parseInt(value) || 0)
   return (
     <div>
       <div className="flex flex-wrap gap-2" role="group" aria-label={field.label}>
         {chipOptions.map(opt => {
           const selected = field.multi ? multiValues.includes(opt.value) : (str === opt.value && !customOpen)
           const primary = field.multi && multiValues[0] === opt.value && multiValues.length > 1
           return (
             <button
               key={opt.value}
               type="button"
               aria-pressed={selected}
               onClick={() => pick(opt.value)}
               className={cn(
                 'min-h-touch px-3.5 py-2 rounded-full border text-sm font-medium transition-colors',
                 selected ? 'bg-brand text-on-brand border-brand' : 'bg-surface text-secondary border-strong hover:border-brand',
               )}
             >
               {opt.label}{primary ? ' · main' : ''}
             </button>
           )
         })}
         {field.customDuration && (
           <button
             type="button"
             aria-pressed={customOpen}
             onClick={() => onFormDataChange({ ...formData, [customKey]: !customOpen })}
             className={cn(
               'min-h-touch px-3.5 py-2 rounded-full border text-sm font-medium transition-colors',
               customOpen ? 'bg-brand text-on-brand border-brand' : 'bg-surface text-secondary border-strong hover:border-brand',
             )}
           >
             More…
           </button>
         )}
              {field.multi && field.allowCustom && (
         <AddOwnChip
           onAdd={addOwn}
           label={field.customLabel}
           placeholder={field.customPlaceholder}
           chipClassName="min-h-touch px-3.5 py-2 rounded-full border border-dashed border-strong bg-surface text-sm font-medium text-secondary hover:border-brand transition-colors"
           testId={`${field.name}-add-own`}
         />
       )}
       </div>
       {customOpen && (
         <div className="mt-3 flex items-center gap-3 max-w-xs">
           <div className="flex-1">
             <label className="block text-xs text-muted mb-1">Hours</label>
             <input type="number" min={0} max={999} value={Math.floor(totalMinutes / 60) || ''} placeholder="0"
                    onChange={(e) => handleFieldChange(field.name, (parseInt(e.target.value) || 0) * 60 + (totalMinutes % 60))}
                    className={baseInputClasses} />
           </div>
           <span className="text-lg font-medium text-muted pt-5">:</span>
           <div className="flex-1">
             <label className="block text-xs text-muted mb-1">Minutes</label>
             <input type="number" min={0} max={59} value={(totalMinutes % 60) || ''} placeholder="0"
                    onChange={(e) => handleFieldChange(field.name, Math.floor(totalMinutes / 60) * 60 + Math.min(parseInt(e.target.value) || 0, 59))}
                    className={baseInputClasses} />
           </div>
         </div>
       )}
     </div>
   )
 }
 case 'custom':
 return field.render ? <>{field.render(formData, onFormDataChange)}</> : null
 case 'parent_picker':
 return (
 <ParentPicker
   allowedTypes={field.allowedParentTypes || []}
   parentType={(formData.parent_type || '') as ParentType | ''}
   parentId={formData.parent_id || null}
   onChange={(fields) => onFormDataChange({ ...formData, ...fields })}
 />
 )
case 'recurrence':
return (
<RecurrencePicker
value={formData.recurrence_rule as RecurrenceRule | null}
onChange={(rule) => {
handleFieldChange('is_recurring', !!rule)
handleFieldChange('recurrence_rule', rule)
handleFieldChange('recurrence_pattern', rule?.frequency || null)
}}
/>
)
case 'place_picker':
return (
<PlacePicker
value={(formData[field.name] as number | null | undefined) ?? null}
onChange={(placeId) => handleFieldChange(field.name, placeId)}
suggestFromText={`${formData.title ?? ''} ${formData.description ?? ''}`.trim()}
/>
)
 default:
 return (
 <input
 type="text"
 name={field.name}
 value={value}
 onChange={(e) => handleFieldChange(field.name, e.target.value)}
 placeholder={field.placeholder}
 className={baseInputClasses}
 required={isRequired}
 />
 )
 }
 }

 const primaryColorClasses = {
 purple: 'bg-info hover:bg-brand-hover focus:ring-info',
 blue: 'bg-brand hover:bg-brand-hover focus:ring-brand',
 green: 'bg-ok hover:bg-ok focus:ring-ok',
 indigo: 'bg-info hover:bg-info focus:ring-info',
 orange: 'bg-warn hover:bg-warn focus:ring-warn',
 }

 const primaryColorClass = primaryColorClasses[primaryColor as keyof typeof primaryColorClasses] || primaryColorClasses.purple

 const gradientClasses = {
 purple: 'from-tint-info-veil to-tint-bad-wash',
 blue: 'from-tint-brand-veil to-tint-info-veil',
 green: 'from-tint-ok-veil to-tint-ok-wash',
 indigo: 'from-tint-info-veil to-tint-brand-veil',
 orange: 'from-tint-warn-veil to-tint-warn-wash',
 }

 const gradientClass = gradientClasses[primaryColor as keyof typeof gradientClasses] || gradientClasses.purple

 return (
 <div className="min-h-dvh bg-gradient-to-br from-surface-alt to-surface-alt">
 {/* A two-column form fills the content area edge to edge, exactly like the
     detail page it is reached from (owner 2026-09-05: /tasks/140 and its edit
     page changed width). Single-column forms keep their centred 896px. */}
 <div className={twoColumn ? 'w-full py-2 sm:py-4' : 'max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8'}>
 {/* Header */}
 {compactHeader ? (
 <div className="mb-4 space-y-2">
 <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-app-meta text-muted">
 <button type="button" onClick={handleCancel} className="inline-flex items-center gap-1 hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
 </nav>
 <h1 className="text-xl sm:text-2xl font-semibold leading-tight text-primary">{title}</h1>
 <p className="text-app-meta text-secondary">{description}</p>
 </div>
 ) : (
 <div className={`bg-gradient-to-r ${gradientClass} rounded-xl sm:rounded-2xl p-3 sm:p-6 mb-3 sm:mb-8 border border-soft shadow-md`}>
 <div className="flex items-center space-x-4 mb-1 sm:mb-4">
 <Button
 variant="ghost"
 size="sm"
 onClick={handleCancel}
 className="hover:bg-surface/50"
 >
 <ArrowLeft className="h-5 w-5 mr-2" />
 Back
 </Button>
 </div>
 <div className="flex items-center space-x-3 sm:space-x-4">
 {icon && (
 <div className={`hidden sm:block bg-gradient-to-r ${gradientClass} p-2.5 sm:p-4 rounded-lg sm:rounded-xl shadow-lg flex-shrink-0`}>
 {icon}
 </div>
 )}
 <div className="flex-1 min-w-0">
 <h1 className="text-xl sm:text-3xl font-bold text-primary mb-1 sm:mb-2">
 {title}
 </h1>
 <p className="text-app-meta sm:text-lg text-secondary">
 {description}
 </p>
 </div>
 </div>
 </div>

 )}
 {/* Form Card */}
 <div className="bg-surface rounded-xl sm:rounded-2xl shadow-xl border border-soft overflow-hidden">
 <form onSubmit={(e) => { setAttempted(true); onSubmit(e) }} className="p-3 sm:p-8" noValidate>
 {error && (
 <div className="mb-6 p-4 bg-tint-bad-soft border border-tint-bad-edge rounded-lg flex items-start space-x-3">
 <AlertCircle className="h-5 w-5 text-bad flex-shrink-0 mt-0.5" />
 <p className="text-sm text-bad">{error}</p>
 </div>
 )}

 {/* Basic Information Section */}
 <div className="mb-6 sm:mb-8">
 {!compactHeader && (
 <div className="flex items-center space-x-2 mb-4 sm:mb-6">
 <Sparkles className="h-5 w-5 text-muted" />
 <h2 className="text-lg sm:text-xl font-semibold text-primary">Basic Information</h2>
 </div>
 )}
 <div className="grid grid-cols-1 gap-y-6 lg:grid-cols-2 lg:gap-x-8">
 {visibleBasic.map((field, i) => (
 <React.Fragment key={field.name}>
 {field.group && visibleBasic[i - 1]?.group?.key !== field.group.key && (
 <div className={cn('min-w-0 lg:col-span-2 -mb-2', i > 0 && 'border-t border-soft pt-5')} data-testid={`form-group-${field.group.key}`}>
 <h2 className="text-app-meta font-bold uppercase tracking-widest text-secondary">{field.group.title}</h2>
 {field.group.blurb && <p className="mt-0.5 text-app-micro text-muted">{field.group.blurb}</p>}
 </div>
 )}
 <div className={field.span === 'half' ? 'min-w-0' : 'min-w-0 lg:col-span-2'}>
 <label className="block text-sm font-semibold text-secondary mb-2">
 {field.label}
 {field.required && (
 <span className="text-bad ml-1.5" title="Required field">*</span>
 )}
 {field.hint && (
 <span className="ml-2 text-app-micro font-normal text-muted">{field.hint}</span>
 )}
 </label>
 {renderField(field)}
 {field.liveHint && field.liveHint(formData[field.name], formData) && (
 <p className="mt-1.5 text-xs text-warn">{field.liveHint(formData[field.name], formData)}</p>
 )}
 {field.helpText && (
 <p className="mt-1.5 text-xs text-muted">{field.helpText}</p>
 )}
 {field.name === 'due_time' && (
 <QuietHoursNotice
 dueTime={formData.due_time as string | undefined}
 leadMinutes={formData.hold_lead_minutes as number | undefined}
 />
 )}
 {field.required && attempted && !formData[field.name] && (
 <p className="mt-1.5 text-xs text-bad flex items-center space-x-1">
 <AlertCircle className="h-3 w-3" />
 <span>This field is required</span>
 </p>
 )}
 </div>
 </React.Fragment>
 ))}
 </div>
 </div>

 {/* Advanced Section */}
 {advancedFields.length > 0 && (
 <div className="mb-8">
 <Accordion title={advancedTitle} defaultOpen={false}>
 <div className="grid grid-cols-1 gap-y-6 pt-4 lg:grid-cols-2 lg:gap-x-8">
 {advancedFields.filter((field) => !field.showWhen || field.showWhen(formData)).map((field) => (
 <div key={field.name} className={field.span === 'half' ? 'min-w-0' : 'min-w-0 lg:col-span-2'}>
 <label className="block text-sm font-semibold text-secondary mb-2">
 {field.label}
 {field.required && (
 <span className="text-bad ml-1.5" title="Required field">*</span>
 )}
 {field.hint && (
 <span className="ml-2 text-app-micro font-normal text-muted">{field.hint}</span>
 )}
 </label>
 {renderField(field)}
 {field.liveHint && field.liveHint(formData[field.name], formData) && (
 <p className="mt-1.5 text-xs text-warn">{field.liveHint(formData[field.name], formData)}</p>
 )}
 {field.helpText && (
 <p className="mt-1.5 text-xs text-muted">{field.helpText}</p>
 )}
 {field.name === 'due_time' && (
 <QuietHoursNotice
 dueTime={formData.due_time as string | undefined}
 leadMinutes={formData.hold_lead_minutes as number | undefined}
 />
 )}
 {field.required && attempted && !formData[field.name] && (
 <p className="mt-1.5 text-xs text-bad flex items-center space-x-1">
 <AlertCircle className="h-3 w-3" />
 <span>This field is required</span>
 </p>
 )}
 </div>
 ))}
 </div>
 </Accordion>
 </div>
 )}

 {/* Action Buttons */}
 <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-4 pt-4 sm:pt-6 border-t border-soft">
 <Button 
 type="button" 
 variant="outline" 
 onClick={handleCancel}
 disabled={isLoading}
 className="w-full sm:w-auto px-6"
 >
 {cancelLabel}
 </Button>
 <Button 
 type="submit" 
 className={cn(primaryColorClass, 'w-full sm:w-auto px-6')}
 disabled={isLoading}
 >
 {isLoading ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
 Creating...
 </>
 ) : (
 submitLabel
 )}
 </Button>
 </div>
 </form>
 </div>
 </div>
 </div>
 )
}

